/**
 * Where this account stands, at the top of every page.
 *
 * Three states, three tones, and silence the rest of the time:
 *
 *   trialing   — days remaining, counted down. Quiet until the last two days,
 *                then amber, because "your trial ends tomorrow" is news and
 *                "6 days left" is wallpaper.
 *   past_due   — a payment failed and the grace clock is running. Amber, and
 *                it says nothing is locked yet, because that is true and it is
 *                the thing someone panics about.
 *   blocked    — the trial ended, or grace ran out. Red, and the first thing
 *                it says is that their work is still there.
 *
 * An account in good standing sees NOTHING. A permanent bar reminding a paying
 * customer that they pay is an advert, not information.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiClock, FiAlertTriangle, FiLock } from 'react-icons/fi';
import { billingAPI } from '../../services/api';
import { ROUTES } from '../../constants/constants';

const TONES = {
  info: { bg: '#edeef3', fg: '#3a4a66', border: '#d8dce6', Icon: FiClock },
  warn: { bg: '#f3ebd7', fg: '#8a6a1f', border: '#eadfc0', Icon: FiAlertTriangle },
  stop: { bg: '#f7e8e5', fg: '#9a564a', border: '#eccfc9', Icon: FiLock },
};

/** What to say, or null to say nothing at all. */
export function bannerFor(billing) {
  if (!billing) return null;
  const { status, canCreate, daysLeftInTrial, reason } = billing;

  if (status === 'trialing' && canCreate) {
    const days = Number(daysLeftInTrial);
    if (!Number.isFinite(days)) return null;
    return {
      tone: days <= 2 ? 'warn' : 'info',
      title: days === 0
        ? 'Your free trial ends today'
        : `${days} day${days === 1 ? '' : 's'} left in your free trial`,
      body: 'Choose a plan to carry on drafting, using Vee and adding register entries.',
      cta: 'See plans',
    };
  }

  if (status === 'past_due' && canCreate) {
    return {
      tone: 'warn',
      title: 'We could not take your payment',
      // The reassurance comes first on purpose.
      body: 'Nothing is locked. Update your payment method to keep things that way.',
      cta: 'Fix payment',
    };
  }

  if (!canCreate) {
    return {
      tone: 'stop',
      title: 'New work is paused on this account',
      body: reason || 'Everything you have created is still here to read and download.',
      cta: 'See plans',
    };
  }

  return null;
}

export default function TrialBanner() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);

  useEffect(() => {
    let alive = true;
    billingAPI.me()
      .then((res) => { if (alive) setBilling(res?.data || null); })
      .catch(() => { /* billing being unreachable must never break a page */ });
    return () => { alive = false; };
  }, []);

  const banner = bannerFor(billing);
  if (!banner) return null;

  const tone = TONES[banner.tone];
  const { Icon } = tone;

  return (
    <div
      className="flex items-start gap-2.5 px-4 py-2.5 text-[12.5px] leading-relaxed"
      style={{ background: tone.bg, color: tone.fg, borderBottom: `1px solid ${tone.border}` }}
      role="status"
    >
      <Icon size={15} className="shrink-0 mt-0.5" />
      <div className="flex-1 min-w-0">
        <span className="font-semibold">{banner.title}.</span> {banner.body}
      </div>
      <button
        type="button"
        onClick={() => navigate(ROUTES.PACKAGES)}
        className="shrink-0 font-semibold underline whitespace-nowrap"
        style={{ color: tone.fg }}
      >
        {banner.cta}
      </button>
    </div>
  );
}
